import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useEffect, useState, type FormEvent } from 'react';
import { db } from '../firebase';
import { today } from '../lib/dates';
import { DAY_SHORT, type Repeat } from '../lib/recurrence';
import type { Task } from '../lib/types';
import { useApp } from '../state';
import { t } from '../lib/i18n';

export type Draft = Omit<Task, 'id'>;
export const ICONS = ['⏰', '💧', '🚶', '🧘', '🏋️', '🥗', '🥘', '🛒', '⚖️', '📊', '💊', '🌿', '😴', '🧹', '📚', '💼', '📞', '🎯', '💞', '🍽️', '🎬', '✈️', '☕'];
const TOGETHER_ICONS = new Set(['💞', '🍽️', '🎬', '✈️', '☕']);

/** One-tap starters for things a couple does together. */
const TOGETHER: { title: string; icon: string; time: string; durationMin: number }[] = [
  { title: 'Date night', icon: '💞', time: '20:00', durationMin: 120 },
  { title: 'Dinner out', icon: '🍽️', time: '19:30', durationMin: 90 },
  { title: 'Movie together', icon: '🎬', time: '21:00', durationMin: 120 },
  { title: 'Coffee date', icon: '☕', time: '11:00', durationMin: 60 },
  { title: 'Walk together', icon: '🚶', time: '20:15', durationMin: 30 },
  { title: 'Weekend trip', icon: '✈️', time: '09:00', durationMin: 480 },
];
const DURS = [5, 15, 30, 45, 60, 90];

/** Pick a colour family for the timeline bubble from the icon, so quick-added tasks look right. */
export function kindFor(icon: string): string {
  if (icon === '💧') return 'water';
  if (icon === '🚶') return 'walk';
  if (icon === '🧘' || icon === '🏋️') return 'workout';
  if (icon === '💊') return 'supplement';
  if (icon === '🥗' || icon === '🥘') return 'meal';
  if (TOGETHER_ICONS.has(icon)) return 'together';
  return 'custom';
}

export function nextQuarter(): string {
  const n = new Date();
  const m = Math.ceil((n.getHours() * 60 + n.getMinutes() + 1) / 15) * 15;
  const t = Math.min(m, 23 * 60 + 45);
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

export function newDraft(uid: string, date = today()): Draft {
  return { title: '', icon: '⏰', kind: 'custom', time: nextQuarter(), durationMin: 15, repeat: { type: 'once', date }, assignees: [uid], hint: '', remind: true };
}

/**
 * Bottom sheet to add or edit a task. New items can go to the timeline (with a time) or to the inbox (no time yet).
 */
export function TaskSheet({ id, initial, allowInbox = false, onClose }: { id: string | null; initial: Draft; allowInbox?: boolean; onClose: () => void }) {
  const { duo, members, user } = useApp();
  const uid = user!.uid;
  const [draft, setDraftState] = useState<Draft>(initial);
  const [toInbox, setToInbox] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const setDraft = (p: Partial<Draft>) => setDraftState((d) => ({ ...d, ...p }));
  const setRepeat = (r: Repeat) => setDraft({ repeat: r });

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!duo) return;
    const title = draft.title.trim().slice(0, 120);
    if (!title) { setError(t('Give it a name.')); return; }
    setBusy(true);
    try {
      if (toInbox) {
        await addDoc(collection(db, 'duos', duo.id, 'inbox'), { title, icon: draft.icon ?? '⏰', assignees: draft.assignees, durationMin: draft.durationMin ?? 15, done: false, createdBy: uid, createdAt: serverTimestamp() });
      } else {
        if (draft.repeat.type === 'weekly' && draft.repeat.days.length === 0) { setError(t('Pick at least one day.')); setBusy(false); return; }
        if (draft.repeat.type === 'monthly' && draft.repeat.daysOfMonth.length === 0) { setError(t('Pick at least one day of the month.')); setBusy(false); return; }
        const data = { ...draft, title, kind: draft.kind && draft.kind !== 'custom' ? draft.kind : kindFor(draft.icon ?? ''), hint: (draft.hint ?? '').slice(0, 300) };
        if (id) await updateDoc(doc(db, 'duos', duo.id, 'tasks', id), data);
        else await addDoc(collection(db, 'duos', duo.id, 'tasks'), { ...data, createdBy: uid, createdAt: serverTimestamp() });
      }
      onClose();
    } catch {
      setError(t('Could not save. Check your connection.'));
      setBusy(false);
    }
  }

  async function remove() {
    if (!duo || !id) return;
    await deleteDoc(doc(db, 'duos', duo.id, 'tasks', id));
    onClose();
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet" role="dialog" aria-modal="true" aria-label={id ? t('Edit task') : t('New task')} onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <span className="grabber" aria-hidden />
        <div className="sheet-head">
          <span className="bubble-pick" aria-hidden>{draft.icon}</span>
          <input id="tTitle" autoFocus={!id} placeholder={t('What do you want to do?')} value={draft.title} onChange={(e) => setDraft({ title: e.target.value })} maxLength={120} aria-label={t('Name')} />
        </div>
        {!id && (
          <div className="field">{t('Together')}
            <div className="chips together-chips">{TOGETHER.map((x) => (
              <button type="button" key={x.title} className="chip" onClick={() => { setToInbox(false); setDraft({ ...x, title: t(x.title), kind: 'together', assignees: [] }); }}>{x.icon} {t(x.title)}</button>
            ))}</div>
          </div>
        )}
        <div className="chips" aria-label={t('Icon')}>{ICONS.map((i) => <button type="button" key={i} className="chip" aria-pressed={draft.icon === i} onClick={() => setDraft({ icon: i })}>{i}</button>)}</div>

        {allowInbox && !id && (
          <div className="seg" role="tablist">
            <button type="button" role="tab" aria-selected={!toInbox} onClick={() => setToInbox(false)}>{t('On the timeline')}</button>
            <button type="button" role="tab" aria-selected={toInbox} onClick={() => setToInbox(true)}>{t('Inbox (no time)')}</button>
          </div>
        )}

        {!toInbox && (
          <div className="grid3">
            <label className="field">{t('Time')}<input id="tTime" type="time" value={draft.time} onChange={(e) => setDraft({ time: e.target.value })} /></label>
            <label className="field">{t('Repeat')}
              <select id="tRepeat" value={draft.repeat.type} onChange={(e) => {
                const v = e.target.value;
                setRepeat(v === 'weekly' ? { type: 'weekly', days: [1] } : v === 'monthly' ? { type: 'monthly', daysOfMonth: [1] } : v === 'once' ? { type: 'once', date: today() } : { type: 'daily' });
              }}>
                <option value="once">{t('Once')}</option><option value="daily">{t('Every day')}</option><option value="weekly">{t('Some weekdays')}</option><option value="monthly">{t('Monthly')}</option>
              </select>
            </label>
            {draft.repeat.type === 'once' && (
              <label className="field">{t('Date')}<input id="tDate" type="date" value={draft.repeat.date} onChange={(e) => setRepeat({ type: 'once', date: e.target.value })} /></label>
            )}
          </div>
        )}
        {!toInbox && draft.repeat.type === 'weekly' && (
          <div className="days">{DAY_SHORT.map((d, i) => {
            const r = draft.repeat as { type: 'weekly'; days: number[] };
            const on = r.days.includes(i);
            return <button type="button" key={d} aria-pressed={on} onClick={() => setRepeat({ type: 'weekly', days: on ? r.days.filter((x) => x !== i) : [...r.days, i].sort() })}>{t(d)}</button>;
          })}</div>
        )}
        {!toInbox && draft.repeat.type === 'monthly' && (
          <label className="field">{t('Days of the month (comma separated)')}
            <input id="tDom" defaultValue={(draft.repeat as { daysOfMonth: number[] }).daysOfMonth.join(', ')} onChange={(e) => setRepeat({ type: 'monthly', daysOfMonth: e.target.value.split(',').map((x) => Number(x.trim())).filter((n) => n >= 1 && n <= 31) })} />
          </label>
        )}

        <div className="field">{t('How long')}
          <div className="durs">{DURS.map((m) => <button type="button" key={m} className="chip" aria-pressed={draft.durationMin === m} onClick={() => setDraft({ durationMin: m })}>{m < 60 ? `${m} ${t('min')}` : `${m / 60} ${t('h')}`.replace('1.5', '1½')}</button>)}</div>
        </div>

        <div className="field">{t('For')}
          <div className="seg">
            <button type="button" aria-pressed={draft.assignees.length === 0} onClick={() => setDraft({ assignees: [] })}>{t('Both')}</button>
            {members.map((m) => (
              <button type="button" key={m.uid} aria-pressed={draft.assignees.length === 1 && draft.assignees[0] === m.uid} onClick={() => setDraft({ assignees: [m.uid] })}>
                {m.uid === uid ? t('Me') : m.displayName}
              </button>
            ))}
          </div>
        </div>

        {!toInbox && (
          <>
            <label className="field">{t('Note')}<textarea id="tHint" rows={2} value={draft.hint ?? ''} onChange={(e) => setDraft({ hint: e.target.value })} maxLength={300} /></label>
            <label className="check"><input id="tRemind" type="checkbox" checked={draft.remind !== false} onChange={(e) => setDraft({ remind: e.target.checked })} /> {t('Send a reminder')}</label>
          </>
        )}
        {error && <p className="error" role="alert">{error}</p>}
        <div className="row wrap">
          <button className="btn primary big" style={{ flex: 1 }} disabled={busy}>{id ? t('Save') : toInbox ? t('Add to inbox') : t('Add to timeline')}</button>
          {id && <button type="button" className="btn danger" onClick={remove}>{t('Delete')}</button>}
          <button type="button" className="btn" onClick={onClose}>{t('Cancel')}</button>
        </div>
      </form>
    </div>
  );
}
